import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Button from "../../components/Button";
import Card from "../../components/Card";
import LoadingSpinner from "../../components/LoadingSpinner";
import UserDetailView from "../../components/UserDetailView";
import { api } from "../../services/api";

export default function AdminUserDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [subjects, setSubjects] = useState([]);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    const fetchSubjects = async () => {
      try {
        const response = await api.get("/subjects");
        setSubjects(response.subjects || []);
      } catch (error) {
        console.error("Failed to fetch subjects:", error);
      }
    };
    fetchSubjects();
  }, []);

  useEffect(() => {
    if (id === "new") {
      // New user creation mode
      setFormData({
        email: "",
        password: "",
        firstName: "",
        lastName: "",
        role: "student",
        phone: "",
        cid: "",
        isVerified: false,
      });
      setLoading(false);
      return;
    }
    loadUser();
  }, [id]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const loadUser = async () => {
    try {
      const response = await api.get(`/admin/users/${id}`);
      setUser(response.user);
      setStats(response.stats);
      setFormData({
        firstName: response.user.name.first,
        lastName: response.user.name.last,
        email: response.user.email,
        phone: response.user.phone,
        role: response.user.role,
        cid: response.user.cid || "",
        isVerified: response.user.isVerified,
        bio: response.user.profile?.bio || "",
        subjects: response.user.profile?.subjects || [],
        availability: response.user.profile?.availability || [],
      });
    } catch (error) {
      toast.error("Failed to load user");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    try {
      if (id === "new") {
        // Create new user
        const response = await api.post("/admin/users", {
          email: formData.email,
          password: formData.password,
          name: {
            first: formData.firstName,
            last: formData.lastName,
          },
          role: formData.role,
          phone: formData.phone,
          cid: formData.cid,
        });
        toast.success("User created successfully");
        navigate(`/admin/users/${response.user._id}`);
      } else {
        // Update existing user
        const payload = {
          email: formData.email,
          name: {
            first: formData.firstName,
            last: formData.lastName,
          },
          role: formData.role,
          phone: formData.phone,
          cid: formData.cid,
          isVerified: formData.isVerified,
        };

        // Include profile for tutors
        if (user.role === "tutor") {
          payload.profile = {
            bio: formData.bio,
            subjects: formData.subjects.filter((s) => s.subjectId),
            availability: formData.availability,
          };
        }

        await api.put(`/admin/users/${id}`, payload);
        toast.success("User updated successfully");
        setEditing(false);
        loadUser();
      }
    } catch (error) {
      toast.error(error.error || "Operation failed");
    }
  };

  const handleResetPassword = async () => {
    const newPassword = prompt("Enter new password (min 10 characters):");
    if (!newPassword || newPassword.length < 10) {
      toast.error("Password must be at least 10 characters");
      return;
    }

    try {
      await api.post(`/admin/users/${id}/reset-password`, { newPassword });
      toast.success("Password reset successfully");
    } catch (error) {
      toast.error(error.error || "Password reset failed");
    }
  };

  if (loading) {
    return <LoadingSpinner size="lg" className="py-16" />;
  }

  const isNewUser = id === "new";

  if (!isNewUser && !user) {
    return <div className="p-8">User not found</div>;
  }

  const addAvailability = () => {
    setFormData({
      ...formData,
      availability: [
        ...formData.availability,
        { dayOfWeek: 1, startTime: "09:00", endTime: "17:00" },
      ],
    });
  };

  const updateAvailability = (index, field, value) => {
    const newAvailability = [...formData.availability];
    newAvailability[index] = { ...newAvailability[index], [field]: value };
    setFormData({ ...formData, availability: newAvailability });
  };

  const removeAvailability = (index) => {
    setFormData({
      ...formData,
      availability: formData.availability.filter((_, i) => i !== index),
    });
  };

  const addSubject = () => {
    setFormData({
      ...formData,
      subjects: [
        ...formData.subjects,
        { subjectId: "", expertiseLevel: "intermediate" },
      ],
    });
  };

  const updateSubject = (index, field, value) => {
    const newSubjects = [...formData.subjects];
    newSubjects[index] = { ...newSubjects[index], [field]: value };
    setFormData({ ...formData, subjects: newSubjects });
  };

  const removeSubject = (index) => {
    setFormData({
      ...formData,
      subjects: formData.subjects.filter((_, i) => i !== index),
    });
  };

  const daysOfWeek = [
    { value: 0, label: "Sunday" },
    { value: 1, label: "Monday" },
    { value: 2, label: "Tuesday" },
    { value: 3, label: "Wednesday" },
    { value: 4, label: "Thursday" },
    { value: 5, label: "Friday" },
    { value: 6, label: "Saturday" },
  ];

  const expertiseLevels = [
    { value: "beginner", label: "Beginner" },
    { value: "intermediate", label: "Intermediate" },
    { value: "advanced", label: "Advanced" },
  ];

  return (
    <>
      <UserDetailView
        headerTitle={
          isNewUser
            ? "Create New User"
            : `${user.name?.first} ${user.name?.last}`
        }
        backHref="/admin/users"
        isNewUser={isNewUser}
        user={user}
        stats={stats}
        editing={editing}
        onEdit={() => setEditing(true)}
        onSave={handleUpdate}
        onCancel={() => {
          if (isNewUser) {
            navigate("/admin/users");
          } else {
            setEditing(false);
            loadUser();
          }
        }}
        onResetPassword={handleResetPassword}
        formData={formData}
        onChange={handleChange}
        showVerifyCheckbox={!isNewUser && editing}
        loading={loading}
        showStats
        showStatus
        canEdit
        canResetPassword
      />

      {!isNewUser && user?.role === "tutor" && editing && (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
          <Card>
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-text opacity-80 mb-2">
                  Bio
                </label>
                <textarea
                  name="bio"
                  rows="4"
                  className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-primary focus:border-primary bg-surface text-text"
                  value={formData.bio}
                  onChange={handleChange}
                  placeholder="Tell students about your teaching experience..."
                />
              </div>

              <div>
                <div className="flex justify-between items-center mb-4">
                  <label className="block text-sm font-medium text-text opacity-80">
                    Subjects
                  </label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={addSubject}
                  >
                    + Add Subject
                  </Button>
                </div>
                {formData.subjects.map((subject, index) => (
                  <div key={index} className="flex gap-4 mb-2 items-center">
                    <select
                      value={subject.subjectId?._id || subject.subjectId}
                      onChange={(e) =>
                        updateSubject(index, "subjectId", e.target.value)
                      }
                      className="flex-1 px-3 py-2 border border-border rounded-md bg-surface text-text"
                    >
                      <option value="">Select Subject</option>
                      {subjects.map((s) => (
                        <option key={s._id} value={s._id}>
                          {s.name} ({s.code})
                        </option>
                      ))}
                    </select>
                    <select
                      value={subject.expertiseLevel}
                      onChange={(e) =>
                        updateSubject(index, "expertiseLevel", e.target.value)
                      }
                      className="px-3 py-2 border border-border rounded-md bg-surface text-text"
                    >
                      {expertiseLevels.map((level) => (
                        <option key={level.value} value={level.value}>
                          {level.label}
                        </option>
                      ))}
                    </select>
                    <Button
                      type="button"
                      variant="danger"
                      size="sm"
                      onClick={() => removeSubject(index)}
                    >
                      Remove
                    </Button>
                  </div>
                ))}
                {formData.subjects.length === 0 && (
                  <p className="text-text/70 text-sm">
                    No subjects selected. Click "Add Subject" to add.
                  </p>
                )}
              </div>

              <div>
                <div className="flex justify-between items-center mb-4">
                  <label className="block text-sm font-medium text-text opacity-80">
                    Availability
                  </label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={addAvailability}
                  >
                    + Add Time Slot
                  </Button>
                </div>
                {formData.availability.map((slot, index) => (
                  <div key={index} className="flex gap-4 mb-2 items-center">
                    <select
                      value={slot.dayOfWeek}
                      onChange={(e) =>
                        updateAvailability(
                          index,
                          "dayOfWeek",
                          parseInt(e.target.value)
                        )
                      }
                      className="px-3 py-2 border border-border rounded-md bg-surface text-text"
                    >
                      {daysOfWeek.map((day) => (
                        <option key={day.value} value={day.value}>
                          {day.label}
                        </option>
                      ))}
                    </select>
                    <input
                      type="time"
                      value={slot.startTime}
                      onChange={(e) =>
                        updateAvailability(index, "startTime", e.target.value)
                      }
                      className="px-3 py-2 border border-border rounded-md bg-surface text-text"
                    />
                    <span className="text-text">to</span>
                    <input
                      type="time"
                      value={slot.endTime}
                      onChange={(e) =>
                        updateAvailability(index, "endTime", e.target.value)
                      }
                      className="px-3 py-2 border border-border rounded-md bg-surface text-text"
                    />
                    <Button
                      type="button"
                      variant="danger"
                      size="sm"
                      onClick={() => removeAvailability(index)}
                    >
                      Remove
                    </Button>
                  </div>
                ))}
                {formData.availability.length === 0 && (
                  <p className="text-text/70 text-sm">
                    No availability set. Click "Add Time Slot" to add.
                  </p>
                )}
              </div>
            </div>
          </Card>
        </div>
      )}
    </>
  );
}
